import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';

import { canActivateWebID, getWebIDByIdPublic } from '../../services/webids';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../ui/LoadingSpinner';
import { WebIDDisabledMessage } from '../activation/WebIDActivationPrompt';

/**
 * @component ActivationGuard
 * @description Guard que protege las rutas de activación y onboarding
 * Verifica que el WebID esté en el estado correcto para la acción solicitada
 */

interface ActivationGuardProps {
  children: React.ReactNode;
  /** Tipo de protección */
  guardType: 'activation' | 'onboarding' | 'profile';
}

interface GuardState {
  loading: boolean;
  canAccess: boolean;
  redirectTo?: string;
  error?: string;
  webIdData?: any;
}

const ActivationGuard: React.FC<ActivationGuardProps> = ({ 
  children, 
  guardType 
}) => {
  const { webId } = useParams<{ webId: string }>();
  const { user, loading: authLoading } = useAuth();
  
  const [state, setState] = useState<GuardState>({
    loading: true,
    canAccess: false,
  });

  useEffect(() => {
    const checkAccess = async () => {
      if (!webId) {
        setState({
          loading: false,
          canAccess: false,
          error: 'ID de perfil no válido'
        });
        return;
      }

      try {
        // Obtener datos del WebID
        const webIdData = await getWebIDByIdPublic(webId);

        // Verificaciones según el tipo de guard
        switch (guardType) {
          case 'activation':
            await checkActivationAccess(webIdData);
            break;
          case 'onboarding':
            await checkOnboardingAccess(webIdData);
            break;
          case 'profile':
            await checkProfileAccess(webIdData);
            break;
        }

      } catch (error) {
        console.error('Error verificando acceso:', error);
        setState({
          loading: false,
          canAccess: false,
          error: 'Error al verificar el perfil de emergencia'
        });
      }
    };

    if (!authLoading) {
      checkAccess();
    }
  }, [webId, user, authLoading, guardType]);

  // Verificar acceso para activación
  const checkActivationAccess = async (webIdData: any) => {
    // Verificar que esté habilitado
    if (!webIdData.isEnabled) {
      setState({
        loading: false,
        canAccess: false,
        error: 'disabled'
      });
      return;
    }

    // Verificar que no esté ya completamente configurado
    if (webIdData.activationStatus === 'onboarding_completed') {
      setState({
        loading: false,
        canAccess: false,
        redirectTo: `/profile/${webId}`
      });
      return;
    }

    // Si ya está activado por este usuario, redirigir al onboarding
    if (user && webIdData.userId === user.uid && webIdData.activationStatus === 'activated') {
      setState({
        loading: false,
        canAccess: false,
        redirectTo: `/onboarding/${webId}`
      });
      return;
    }

    // Verificar que puede ser activado
    const canActivate = await canActivateWebID(webId!);
    
    setState({
      loading: false,
      canAccess: canActivate.canActivate,
      error: canActivate.canActivate ? undefined : canActivate.reason,
      webIdData: canActivate.webIdData
    });
  };

  // Verificar acceso para onboarding
  const checkOnboardingAccess = async (webIdData: any) => {
    // Debe estar autenticado
    if (!user) {
      setState({
        loading: false,
        canAccess: false,
        redirectTo: `/activate/${webId}`
      });
      return;
    }

    // Debe ser el propietario del WebID
    if (webIdData.userId !== user.uid) {
      setState({
        loading: false,
        canAccess: false,
        error: 'No tienes acceso a este perfil de emergencia'
      });
      return;
    }

    // Debe estar en estado 'activated'
    if (webIdData.activationStatus !== 'activated') {
      if (webIdData.activationStatus === 'onboarding_completed') {
        setState({
          loading: false,
          canAccess: false,
          redirectTo: `/profile/${webId}`
        });
      } else {
        setState({
          loading: false,
          canAccess: false,
          redirectTo: `/activate/${webId}`
        });
      }
      return;
    }

    setState({
      loading: false,
      canAccess: true,
      webIdData
    });
  };

  // Verificar acceso para perfil
  const checkProfileAccess = async (webIdData: any) => {
    // El perfil debe estar completamente configurado para ser público
    if (webIdData.activationStatus !== 'onboarding_completed') {
      setState({
        loading: false,
        canAccess: false,
        redirectTo: `/activate/${webId}`
      });
      return;
    }

    setState({
      loading: false,
      canAccess: true,
      webIdData
    });
  };

  // Renderizar según el estado
  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Verificando acceso..." />
      </div>
    );
  }

  if (state.error === 'disabled') {
    return <WebIDDisabledMessage webId={webId!} />;
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-lg shadow-lg p-6 text-center max-w-md w-full mx-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Acceso Denegado
          </h1>
          
          <p className="text-gray-600 mb-4">
            {state.error}
          </p>
          
          <button
            onClick={() => window.history.back()}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  if (state.redirectTo) {
    return <Navigate to={state.redirectTo} replace />;
  }

  if (!state.canAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-lg shadow-lg p-6 text-center max-w-md w-full mx-4">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Acceso No Permitido
          </h1>
          
          <p className="text-gray-600 mb-4">
            No puedes acceder a esta página en este momento.
          </p>
          
          <button
            onClick={() => window.history.back()}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ActivationGuard;

/**
 * @component AuthGuard
 * @description Guard que requiere autenticación
 */

interface AuthGuardProps {
  children: React.ReactNode;
  redirectTo?: string;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  redirectTo = '/' 
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Verificando autenticación..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};
